// What a puzzle is: its kind, and the motifs it was tagged with.

export type Kind = "tactic" | "only_move" | "blunder" | "opening";

export const KIND: Record<Kind, { label: string; prompt: string; why: string }> = {
  tactic: { label: "Tactic", prompt: "Find the win you missed", why: "A forced win was on the board. There is one answer." },
  only_move: {
    label: "Only move",
    prompt: "Find the only move that holds",
    why: "Only one move held here — everything else gave the position away. There is one answer.",
  },
  blunder: {
    label: "Blunder",
    prompt: "Play any move that holds",
    why: "There was nothing to find here — the move you played threw the position away. Any move that holds is right.",
  },
  opening: { label: "Opening", prompt: "Play the book move", why: "You left theory here, and it cost you." },
};

export const kindOf = (puzzle: { kind?: string }): Kind => (puzzle.kind && puzzle.kind in KIND ? (puzzle.kind as Kind) : "tactic");

/** The words for one puzzle. Only an opening puzzle varies: one from your own
 *  repertoire asks for your line, not for theory in general. */
export function kindText(puzzle: { kind?: string; opening?: { source?: string } | null }) {
  const kind = kindOf(puzzle);
  if (kind === "opening" && puzzle.opening?.source === "repertoire") {
    return { label: KIND.opening.label, prompt: "Play your line", why: "You had this prepared and played something else." };
  }
  return KIND[kind];
}

/** Tags whose name as written does not read as words. */
const TAG_NAMES: Record<string, string> = {
  freeMaterial: "free material",
  movesAttackedPiece: "move the attacked piece",
  takesAttacker: "take the attacker",
  blocksAttack: "block the attack",
  defendsPiece: "defend the piece",
  counterAttack: "counter-attack",
  escapesCheck: "escape the check",
  stopsMate: "stop the mate",
  capturingDefender: "capture the defender",
  xRayAttack: "x-ray attack",
};

/** What each motif is, in a line. */
export const TAG_MEANS: Record<string, string> = {
  fork: "One piece attacks two things at once.",
  pin: "A piece cannot move without exposing what is behind it.",
  skewer: "The valuable piece is attacked and must move, and what is behind it falls.",
  hangingPiece: "A piece nothing defended. It was there to be taken.",
  freeMaterial: "Attacked more times than it was defended: taking it simply won.",
  trappedPiece: "A piece with nowhere to go gets rounded up.",
  discoveredAttack: "A piece moves away and the one behind it attacks.",
  doubleCheck: "Two pieces give check at once; only the king can answer.",
  sacrifice: "Material given up for something bigger.",
  deflection: "A defender is pulled away from what it was guarding.",
  attraction: "A piece is lured onto the square where it gets hit.",
  xRayAttack: "An attack that goes through a piece to what is behind it.",
  clearance: "A piece vacates a line or square another piece needs.",
  interference: "A piece is put in the way of a defender's line.",
  intermezzo: "An in-between move before the expected recapture.",
  capturingDefender: "The piece doing the defending is taken first.",
  quietMove: "No check, no capture: a move that makes a threat there is no answer to.",
  defensiveMove: "The move that keeps the position together.",
  advancedPawn: "A pawn close to promotion decides it.",
  promotion: "A pawn gets through.",
  underPromotion: "Promoting to something other than a queen was the point.",
  exposedKing: "A king with too little cover around it.",
  backRankMate: "Mate on the back rank, behind its own pawns.",
  smotheredMate: "Mate by a knight against a king walled in by its own pieces.",
  enPassant: "The capture in passing was the move.",
  castling: "Castling was the move.",
  movesAttackedPiece: "Something of yours was attacked, and the answer was to move it.",
  takesAttacker: "Something of yours was attacked, and the answer was to take the attacker.",
  blocksAttack: "Something of yours was attacked, and the answer was to put something in the way.",
  defendsPiece: "Something of yours was attacked, and the answer was to defend it.",
  counterAttack: "The threat was left standing and answered with a bigger one.",
  escapesCheck: "You were in check, and only one way out held.",
  stopsMate: "Mate was threatened, and one move stopped it.",
};

/** Our motif names are Lichess's theme names, except the ones Lichess has no
 *  theme for: those point at the nearest thing it has. */
const LICHESS_NEAREST: Record<string, string> = {
  freeMaterial: "hangingPiece",
  movesAttackedPiece: "defensiveMove",
  takesAttacker: "defensiveMove",
  blocksAttack: "defensiveMove",
  defendsPiece: "defensiveMove",
  counterAttack: "defensiveMove",
  escapesCheck: "defensiveMove",
  stopsMate: "defensiveMove",
};
export const lichessTheme = (tag: string): string => LICHESS_NEAREST[tag] || tag;

/** The tags that say how a position was held rather than how it was won. */
export const DEFENCE_TAGS = new Set(["movesAttackedPiece", "takesAttacker", "blocksAttack", "defendsPiece", "counterAttack", "escapesCheck", "stopsMate", "defensiveMove"]);

/** Tags that only repeat what the badge already says. */
export const HIDDEN_TAGS = new Set(["blunder", "onlyMove", "opening", "deviation", "book", "repertoire"]);

/** "backRankMate" → "back-rank mate", "mateIn2" → "mate in 2". */
export function humanTag(tag: string): string {
  if (TAG_NAMES[tag]) return TAG_NAMES[tag];
  const mate = tag.match(/^mateIn(\d+)$/);
  if (mate) return `mate in ${mate[1]}`;
  return tag
    .replace(/^xRay/, "x-ray")
    .replace(/backRank/, "back-rank")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase();
}
